import { Magicpen } from '@myua/icons/dist/linear16';
import { Popover, Spin } from '@myua/kit';
import React from 'react';

import { api } from '../../../../../general/apiScenarios';
import {
  EMPTY_VALUE_PLACEHOLDER,
  PLOT_RECOGNITION_LABELS,
  PLOT_RECOGNITION_LIST_FIELDS,
  PLOT_RECOGNITION_STATUS_LABELS,
  PlotRecognition,
  PlotRecognitionField,
  PlotRecognitionInfo,
  getPlotRecognition,
  getPlotRecognitionInfo,
  narrativeStructureToText,
} from '../../../../../general/modelPlotRecognition';

import './styles.scss';

/**
 * Read-only просмотр AI-разметки сюжета прямо из списка файлов (MYUA-16423).
 *
 * В листинге на ноде есть только флаг `has_plot_recognition` и статус, поэтому сами поля
 * разметки догружаем по клику — `GET /bl/creatives?node_id=<id>` — и кэшируем в состоянии
 * компонента: повторное открытие поповера запроса не делает.
 */

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: PlotRecognition; info: PlotRecognitionInfo };

const EmptyValue = () => (
  <span className="ch-plot-recognition-popover__empty">{EMPTY_VALUE_PLACEHOLDER}</span>
);

const FieldLabel: React.FC<{ field: PlotRecognitionField; isManual: boolean }> = ({
  field,
  isManual,
}) => (
  <div className="ch-plot-recognition-popover__label">
    {PLOT_RECOGNITION_LABELS[field]}
    {isManual && (
      <span
        className="ch-plot-recognition-popover__manual"
        title="Value was set manually — the ML pipeline no longer overwrites it"
      >
        manual
      </span>
    )}
  </div>
);

const TagList: React.FC<{ values: string[] }> = ({ values }) =>
  values.length === 0 ? (
    <EmptyValue />
  ) : (
    <div className="ch-plot-recognition-popover__tags">
      {values.map((value) => (
        <span key={value} className="ch-plot-recognition-popover__tag">
          {value}
        </span>
      ))}
    </div>
  );

const TextValue: React.FC<{ value: string }> = ({ value }) =>
  value.trim() === '' ? (
    <EmptyValue />
  ) : (
    <div className="ch-plot-recognition-popover__text">{value}</div>
  );

const Content: React.FC<{ state: LoadState }> = ({ state }) => {
  if (state.kind === 'loading' || state.kind === 'idle') {
    return (
      <div className="ch-plot-recognition-popover ch-plot-recognition-popover_loading">
        <Spin size="small" />
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="ch-plot-recognition-popover">
        <div className="ch-plot-recognition-popover__note">Failed to load AI metadata.</div>
      </div>
    );
  }

  const { data, info } = state;
  const isManual = (field: PlotRecognitionField) => info.userEditedFields.includes(field);
  const statusLabel =
    info.status === null ? 'no data yet' : PLOT_RECOGNITION_STATUS_LABELS[info.status];

  return (
    <div className="ch-plot-recognition-popover">
      <div className="ch-plot-recognition-popover__status">
        Plot recognition: <b>{statusLabel}</b>
        {info.status === 'error' && info.error ? ` — ${info.error}` : null}
      </div>

      {PLOT_RECOGNITION_LIST_FIELDS.map((field) => (
        <div className="ch-plot-recognition-popover__field" key={field}>
          <FieldLabel field={field} isManual={isManual(field)} />
          <TagList values={data[field]} />
        </div>
      ))}

      <div className="ch-plot-recognition-popover__field">
        <FieldLabel field="narrative_structure" isManual={isManual('narrative_structure')} />
        <TextValue value={narrativeStructureToText(data.narrative_structure)} />
      </div>

      <div className="ch-plot-recognition-popover__field">
        <FieldLabel field="analyst_insight" isManual={isManual('analyst_insight')} />
        <TextValue value={data.analyst_insight} />
      </div>

      <div className="ch-plot-recognition-popover__note">
        Read-only — edit AI metadata in the Metadata tab of Edit creative.
      </div>
    </div>
  );
};

/**
 * Триггером может быть как иконка-робот в столбце `Name`, так и статус в столбце `AI Metadata`
 * (MYUA-16736): наружу отдаём одинаковый поповер, различаются только содержимое и классы.
 */
export const PlotRecognitionPopover: React.FC<{
  nodeId: number;
  className?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
}> = ({
  nodeId,
  className = 'ch-file-manager__plot-recognition-indicator',
  ariaLabel = 'Show AI metadata',
  children,
}) => {
  const [open, setOpen] = React.useState(false);
  const [state, setState] = React.useState<LoadState>({ kind: 'idle' });
  // Ячейки виртуализированного списка легко переживают ответ сервера, поэтому
  // результат устаревшего запроса игнорируем.
  const requestID = React.useRef(0);

  React.useEffect(() => () => void (requestID.current += 1), []);

  const load = async () => {
    const currentRequest = (requestID.current += 1);
    setState({ kind: 'loading' });
    const result = await api.creative.loadCreativeForNodeScenario(nodeId);
    if (currentRequest !== requestID.current) return;
    const creative = result.success ? result.data.response[0] : undefined;
    if (!creative) {
      setState({ kind: 'error' });
      return;
    }
    setState({
      kind: 'ready',
      data: getPlotRecognition(creative.plot_recognition),
      info: getPlotRecognitionInfo(creative.plot_recognition),
    });
  };

  const onOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    // Данные креатива не меняются, пока открыт список, — грузим один раз.
    if (nextOpen && (state.kind === 'idle' || state.kind === 'error')) {
      void load();
    }
  };

  // Иконка живёт внутри ссылки на креатив: клик по ней не должен уводить со страницы.
  const stopNavigation = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={['click']}
      placement="bottomLeft"
      title="AI metadata"
      content={<Content state={state} />}
      overlayClassName="ch-plot-recognition-popover__overlay"
    >
      <span
        className={className}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={stopNavigation}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          stopNavigation(e);
          onOpenChange(!open);
        }}
      >
        {children ?? <Magicpen />}
      </span>
    </Popover>
  );
};
